import type { ListingContactPreview } from "@wags/catalog/pure.ts";
import type { CompatibilityListingInput } from "@wags/matchmaker/pure.ts";
import {
    type ContactMessageIntent,
    DEFAULT_CONTACT_MESSAGE_INTENT,
} from "@wags/profile-contracts/contact-session.contract.ts";
import { useSyncExternalStore } from "react";
import {
    EVENT_BLOCK,
    newKitsInputId,
    type TriggerEvent,
} from "./wags-ui-message";

export type ChatFabMode = "default" | "above-bottom-nav";

export type ChatFabDock = "center" | "content";

export type PendingContact = {
    id: string;
    name: string;
    preview?: ListingContactPreview;
    source: string;
    intent: ContactMessageIntent;
};

export type ReachOutSheetRequest = Omit<PendingContact, "name"> & {
    name: string | undefined;
    handedOff?: boolean;
    question?: string;
};

export type CompatQuestions = {
    listingId: string;
    listingName: string;

    kind: "panel" | "feedReveal";
};

export type ListingTransaction =
    | {
          kind: "price";
          price: string;
          compactPrice: string;
          oneTap?: boolean;
      }
    | {
          kind: "contact";
          label: string;
          intent: ContactMessageIntent;
      }
    | {
          kind: "link";
          link: {
              href: string;
              label: string;
              type: "application" | "website" | "browse";
          };
      };

export type ChatPageContext = {
    listingId?: string;
    listingName?: string;
    contactPreview?: ListingContactPreview;
    compatibility?: CompatibilityListingInput;
    hideClosedPill?: boolean;

    transaction?: ListingTransaction;
    missingDetail?: MissingListingDetail;
};

export type MissingListingDetail = "fee" | "delivery";

export type PillHoldReason = "unread" | "covers-action-row";

type PendingUserMessage = { text: string; inputId: string };

type ChatControllerState = {
    pendingOpen: number | null;

    collapseSignal: number;

    pageContext: ChatPageContext | null;

    pendingContact: PendingContact | null;

    reachOutSheet: ReachOutSheetRequest | null;

    reachOutSheetExiting: boolean;

    compatQuestions: CompatQuestions | null;

    owedFitReveal: CompatQuestions | null;
    fabMode: ChatFabMode;

    fabDock: ChatFabDock;

    chatSuppressed: boolean;

    doomscrollOpen: boolean;

    chatOpen: boolean;

    pendingTriggerEvent: TriggerEvent | null;

    pendingUserMessage: PendingUserMessage | null;

    navRevealed: boolean;

    dockAction: { busy: boolean } | null;

    pillHold: { owner: symbol; reason: PillHoldReason } | null;
};

let state: ChatControllerState = {
    pendingOpen: null,
    collapseSignal: 0,
    dockAction: null,
    pageContext: null,
    pendingContact: null,
    reachOutSheet: null,
    reachOutSheetExiting: false,
    compatQuestions: null,
    owedFitReveal: null,
    fabMode: "default",
    fabDock: "center",
    chatSuppressed: false,
    doomscrollOpen: false,
    chatOpen: false,
    pendingTriggerEvent: null,
    pendingUserMessage: null,
    navRevealed: false,
    pillHold: null,
};

const subscribers = new Set<() => void>();

function setState(patch: Partial<ChatControllerState>) {
    state = { ...state, ...patch };
    for (const cb of subscribers) cb();
}

function subscribe(cb: () => void) {
    subscribers.add(cb);
    return () => {
        subscribers.delete(cb);
    };
}

export function useChatController<T>(
    selector: (s: ChatControllerState) => T,
): T {
    return useSyncExternalStore(
        subscribe,
        () => selector(state),
        () => selector(state),
    );
}

export function getChatControllerState(): ChatControllerState {
    return state;
}

export function setChatPageContext(context: ChatPageContext): () => void {
    setState({ pageContext: context });
    return () => {
        if (state.pageContext === context) setState({ pageContext: null });
    };
}

export const REACH_SHEET_EXIT_LATCH_MS = 450;

let reachOutSheetExitTimer: ReturnType<typeof setTimeout> | null = null;

function cancelReachOutSheetExitTimer() {
    if (reachOutSheetExitTimer !== null) {
        clearTimeout(reachOutSheetExitTimer);
        reachOutSheetExitTimer = null;
    }
}

function reachOutSheetYieldPatch(): Partial<ChatControllerState> {
    cancelReachOutSheetExitTimer();
    return { reachOutSheet: null, reachOutSheetExiting: false };
}

function reachOutSheetClosePatch(): Partial<ChatControllerState> {
    cancelReachOutSheetExitTimer();
    reachOutSheetExitTimer = setTimeout(() => {
        reachOutSheetExitTimer = null;
        setState({ reachOutSheetExiting: false });
    }, REACH_SHEET_EXIT_LATCH_MS);
    return { reachOutSheet: null, reachOutSheetExiting: true };
}

let lastOpenRequestId = 0;

function queueOpenPatch(): Partial<ChatControllerState> {
    lastOpenRequestId += 1;
    return {
        pendingOpen: lastOpenRequestId,
        ...reachOutSheetYieldPatch(),
    };
}

let dockActionRun: (() => void) | null = null;

export function setDockAction(
    action: { run: () => void; busy: boolean } | null,
) {
    dockActionRun = action?.run ?? null;
    const busy = action?.busy ?? null;
    if ((state.dockAction?.busy ?? null) !== busy)
        setState({ dockAction: action ? { busy: action.busy } : null });
}

export function runDockAction() {
    dockActionRun?.();
}

let contactVerifiedRefresh: (() => Promise<void>) | null = null;

export function registerContactVerifiedRefresh(refresh: () => Promise<void>) {
    contactVerifiedRefresh = refresh;
    return () => {
        if (contactVerifiedRefresh === refresh) contactVerifiedRefresh = null;
    };
}

export async function refreshAfterContactVerified() {
    await contactVerifiedRefresh?.();
}

export function requestOpenChat() {
    setState(queueOpenPatch());
}

export type ListingQuestionSource = "faq_follow_up" | "faq_typed";

export function requestListingQuestion(
    id: string,
    question: string,
    source: ListingQuestionSource,
): string {
    const inputId = newKitsInputId();
    setState({
        pendingOpen: null,
        pendingContact: null,
        compatQuestions: null,
        pendingTriggerEvent: null,
        pendingUserMessage: {
            text: `${question}${EVENT_BLOCK} Listing: /dog/${encodeURIComponent(id)} Source: ${source}`,
            inputId,
        },
    });
    return inputId;
}

export function requestCollapseChat() {
    setState({ collapseSignal: state.collapseSignal + 1, pendingOpen: null });
}

export function requestContactConfirmation(
    id: string,
    name: string,
    preview?: ListingContactPreview,
    source = "unknown",
    intent: ContactMessageIntent = DEFAULT_CONTACT_MESSAGE_INTENT,
) {
    setState({
        ...queueOpenPatch(),
        pendingContact: { id, name, preview, source, intent },
    });
}

export function clearPendingContact() {
    if (state.pendingContact) setState({ pendingContact: null });
}

export function clearPendingUserMessage() {
    if (state.pendingUserMessage) setState({ pendingUserMessage: null });
}

export function openReachOutSheet(
    id: string,
    name: string | undefined,
    preview?: ListingContactPreview,
    source = "reach_out_sheet",
    {
        intent = DEFAULT_CONTACT_MESSAGE_INTENT,
        handedOff = false,
        question,
    }: {
        intent?: ContactMessageIntent;
        handedOff?: boolean;
        question?: string;
    } = {},
) {
    cancelReachOutSheetExitTimer();
    setState({
        reachOutSheet: {
            id,
            name,
            preview,
            source,
            intent,
            handedOff,
            question,
        },
        reachOutSheetExiting: false,
        pendingOpen: null,
    });
}

export function closeReachOutSheet() {
    if (state.reachOutSheet) setState(reachOutSheetClosePatch());
}

export function markNavRevealed() {
    if (!state.navRevealed) setState({ navRevealed: true });
}

export function createPillHold(): (reason: PillHoldReason | null) => void {
    const owner = Symbol("pill-hold");
    return (reason) => {
        if (reason !== null) setState({ pillHold: { owner, reason } });
        else if (state.pillHold?.owner === owner) setState({ pillHold: null });
    };
}

export function clearPendingOpen(id: number): boolean {
    if (state.pendingOpen !== id) return false;
    setState({ pendingOpen: null });
    return true;
}

export function clearTransientChatRequests() {
    if (
        state.pendingOpen !== null ||
        state.pendingContact ||
        state.reachOutSheet ||
        state.compatQuestions ||
        state.owedFitReveal ||
        state.pendingUserMessage ||
        state.pendingTriggerEvent?.name === "dog_page" ||
        state.pendingTriggerEvent?.name === "far_dog"
    ) {
        setState({
            pendingOpen: null,
            pendingContact: null,

            ...(state.reachOutSheet ? reachOutSheetClosePatch() : {}),
            compatQuestions: null,
            owedFitReveal: null,
            pendingUserMessage: null,
            pendingTriggerEvent:
                state.pendingTriggerEvent?.name === "dog_page" ||
                state.pendingTriggerEvent?.name === "far_dog"
                    ? null
                    : state.pendingTriggerEvent,
        });
    }
}

export function requestCompatibilityCheck(
    listingId: string,
    listingName: string,
) {
    setState({
        ...queueOpenPatch(),
        compatQuestions: { listingId, listingName, kind: "panel" },
    });
}

export function clearCompatQuestions() {
    if (state.compatQuestions) setState({ compatQuestions: null });
}

export function requestFitReveal(listingId: string, listingName: string) {
    const request: CompatQuestions = {
        listingId,
        listingName,
        kind: "feedReveal",
    };
    setState({
        ...queueOpenPatch(),
        compatQuestions: request,
        owedFitReveal: request,
    });
}

export function clearOwedFitReveal(listingId: string) {
    if (state.owedFitReveal?.listingId === listingId) {
        setState({ owedFitReveal: null });
    }
}

export function sendTriggerEvent(event: TriggerEvent) {
    setState({ pendingTriggerEvent: { ...event } });
}

export function clearPendingTriggerEvent() {
    if (state.pendingTriggerEvent) setState({ pendingTriggerEvent: null });
}

export function notifyDoomscrollChange(open: boolean) {
    if (state.doomscrollOpen === open) return;
    setState({ doomscrollOpen: open });
}

export function notifyChatOpenChange(open: boolean) {
    if (state.chatOpen === open) return;
    setState({ chatOpen: open });
}

const activeChatSuppressionRequests = new Map<string, Set<symbol>>();

function applyChatSuppression() {
    const next = activeChatSuppressionRequests.size > 0;
    if (next !== state.chatSuppressed) {
        setState({ chatSuppressed: next });
    }
}

export function requestChatSuppression(key: string): () => void {
    const token = Symbol(key);
    const locks = activeChatSuppressionRequests.get(key) ?? new Set<symbol>();
    locks.add(token);
    activeChatSuppressionRequests.set(key, locks);
    applyChatSuppression();

    let released = false;
    return () => {
        if (released) return;
        released = true;
        const activeLocks = activeChatSuppressionRequests.get(key);
        if (!activeLocks) return;

        activeLocks.delete(token);
        if (activeLocks.size === 0) {
            activeChatSuppressionRequests.delete(key);
        }
        applyChatSuppression();
    };
}

const FAB_MODE_PRIORITY: Record<ChatFabMode, number> = {
    default: 0,
    "above-bottom-nav": 1,
};

const activeFabRequests = new Map<symbol, ChatFabMode>();

function applyResolvedFabMode() {
    let next: ChatFabMode = "default";
    for (const mode of activeFabRequests.values()) {
        if (FAB_MODE_PRIORITY[mode] > FAB_MODE_PRIORITY[next]) {
            next = mode;
        }
    }
    if (next !== state.fabMode) setState({ fabMode: next });
}

export function requestChatFabMode(mode: ChatFabMode): () => void {
    const key = Symbol();
    activeFabRequests.set(key, mode);
    applyResolvedFabMode();
    return () => {
        activeFabRequests.delete(key);
        applyResolvedFabMode();
    };
}

const activeFabDockRequests = new Map<symbol, ChatFabDock>();

function applyResolvedFabDock() {
    let next: ChatFabDock = "center";
    for (const dock of activeFabDockRequests.values()) {
        if (dock === "content") next = "content";
    }
    if (next !== state.fabDock) setState({ fabDock: next });
}

export function requestChatFabDock(dock: ChatFabDock): () => void {
    const key = Symbol();
    activeFabDockRequests.set(key, dock);
    applyResolvedFabDock();
    return () => {
        activeFabDockRequests.delete(key);
        applyResolvedFabDock();
    };
}
